import React, { ReactElement, ReactNode } from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import clsx from "clsx";

export type ExpansionPanelProps = {
    faqId: string;
    children: ReactNode;
    defaultExpanded?: boolean;
    expanded?: boolean;
    onChange?: (event: MouseEvent, value: any) => void;
};

const StyledExpansionPanel = styled("div")(({ theme }) => ({
    boxShadow: "none",
    borderTop: `1px solid ${theme.kaltura.palette.tone4}`,
}));

const panelAnimation = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

const StyledQuestionContainer = styled("div")(({ theme }) => ({
    minHeight: 0,
    height: 0,
    visibility: "hidden",
    overflow: "hidden",
    "&.expanded": {
        height: "auto",
        overflow: "visible",
        visibility: "visible",
        animation: `${panelAnimation} .5s ease-out forwards`,
    },
}));

const ExpansionPanel = ({
    faqId,
    children: childrenProp,
    defaultExpanded = false,
    expanded: expandedProp,
    onChange,
}: ExpansionPanelProps) => {
    const { current: isControlled } = React.useRef(expandedProp != null);
    const [expandedState, setExpandedState] = React.useState(defaultExpanded);
    const expanded = isControlled ? expandedProp : expandedState;
    const [summary, ...children] = React.Children.toArray(childrenProp) as ReactElement<any>[];

    const handleChange = (event: MouseEvent) => {
        if (!isControlled) {
            setExpandedState(!expanded);
        }

        if (onChange) {
            onChange(event, !expanded);
        }
    };

    return (
        <StyledExpansionPanel className="kms-ds-help-page-faq-panel">
            {React.cloneElement(
                summary,
                {
                    expanded,
                    onChange: handleChange,
                    summaryId: `faq-${faqId}-summary`,
                    ariaControls: `faq-${faqId}-details`,
                }
            )}
            <StyledQuestionContainer className={clsx("kms-ds-help-page-faq-answer", expanded && "expanded")}>
                <div id={`faq-${faqId}-details`} aria-labelledby={`faq-${faqId}-summary`} role="region">
                    {children}
                </div>
            </StyledQuestionContainer>
        </StyledExpansionPanel>
    );
};

export default ExpansionPanel;
